<template>
  <div class="order-real-picture-upload">
    <div
      class="order-real-picture-upload__grid"
      :class="{ 'order-real-picture-upload__grid--compact': compact }"
      :style="gridStyle"
    >
      <div
        v-for="(imgUrl, idx) in imageUrls"
        :key="`${imgUrl}-${idx}`"
        class="order-real-picture-upload__thumb"
      >
        <img :src="imgUrl" alt="" @click="previewFn(imgUrl)" />
        <span class="order-real-picture-upload__remove" title="删除" @click.stop="removeImage(idx)">
          <i class="el-icon-close"></i>
        </span>
      </div>
      <el-upload
        v-if="imageUrls.length < max"
        class="order-real-picture-upload__trigger"
        :show-file-list="false"
        :disabled="uploading"
        accept="image/jpeg,image/png,image/gif,image/webp"
        :http-request="uploadRequest"
      >
        <div class="order-real-picture-upload__add" :class="{ 'is-loading': uploading }">
          <i v-if="!uploading" class="el-icon-plus order-real-picture-upload__add-icon"></i>
          <i v-else class="el-icon-loading order-real-picture-upload__add-icon"></i>
          <span class="order-real-picture-upload__add-text">{{
            uploading ? "上传中…" : "添加图片"
          }}</span>
        </div>
      </el-upload>
    </div>
    <p class="order-real-picture-upload__hint">
      支持 JPG / PNG / GIF / WebP，单张建议不超过 5MB，最多 {{ max }} 张
    </p>
  </div>
</template>

<script>
import { normalizeUploadedImageUrl } from "@/util/orderRealPicture";

export default {
  name: "OrderRealPictureUpload",
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    max: {
      type: Number,
      default: 8,
    },
    columns: {
      type: Number,
      default: 4,
    },
    /** 鉴别弹窗等场景：固定较小缩略图，不占满整行 */
    compact: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      uploading: false,
    };
  },
  computed: {
    gridStyle() {
      if (this.compact) {
        return {
          gridTemplateColumns: "repeat(auto-fill, 76px)",
          gap: "8px",
        };
      }
      return {
        gridTemplateColumns: `repeat(${this.columns}, minmax(0, 1fr))`,
        gap: "12px",
      };
    },
    imageUrls: {
      get() {
        return Array.isArray(this.value) ? this.value : [];
      },
      set(next) {
        this.$emit("input", next);
      },
    },
  },
  methods: {
    previewFn(url) {
      if (!url) return;
      window.open(url);
    },
    removeImage(index) {
      if (index < 0 || index >= this.imageUrls.length) return;
      const next = this.imageUrls.slice();
      next.splice(index, 1);
      this.imageUrls = next;
    },
    uploadRequest(option) {
      if (this.imageUrls.length >= this.max) {
        this.$message.warning(`最多 ${this.max} 张图片`);
        return;
      }
      const no = `${Date.now()}_${Math.round(Math.random() * 1000)}`;
      const form = new FormData();
      form.append("file", option.file);
      form.append("no", no);
      this.uploading = true;
      this.$https(this.$api.api.uploadImg, form, "POST")
        .then((res) => {
          const body = res && res.data;
          const code = body && body.code;
          const uploadOk =
            body &&
            (code === 200 || code === 1 || code === 0) &&
            body.data != null &&
            !(typeof body.data === "string" && body.data === "null");
          if (uploadOk) {
            const d = body.data;
            const raw =
              typeof d === "string"
                ? d
                : (d && (d.path || d.url || d.fileUrl || d.fullPath)) || "";
            const url = normalizeUploadedImageUrl(String(raw).trim());
            if (url) {
              this.imageUrls = [...this.imageUrls, url];
              if (typeof option.onSuccess === "function") option.onSuccess(body, option.file);
            } else {
              this.$message.error("未返回图片地址");
              if (typeof option.onError === "function") option.onError(new Error("empty url"));
            }
          } else {
            this.$message.error((body && body.msg) || "上传失败");
            if (typeof option.onError === "function") option.onError(new Error("fail"));
          }
        })
        .catch(() => {
          this.$message.error("上传失败");
          if (typeof option.onError === "function") option.onError(new Error("network"));
        })
        .finally(() => {
          this.uploading = false;
        });
    },
  },
};
</script>

<style scoped>
.order-real-picture-upload {
  width: 100%;
}

.order-real-picture-upload__grid {
  display: grid;
  gap: 12px;
  align-items: start;
}

.order-real-picture-upload__thumb {
  position: relative;
  width: 100%;
  aspect-ratio: 1;
  border-radius: 8px;
  overflow: hidden;
  background: #f5f7fa;
  border: 1px solid #e4e7ed;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.order-real-picture-upload__thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  cursor: pointer;
  display: block;
}

.order-real-picture-upload__remove {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 22px;
  height: 22px;
  line-height: 22px;
  text-align: center;
  background: rgba(0, 0, 0, 0.45);
  color: #fff;
  border-radius: 50%;
  cursor: pointer;
  font-size: 12px;
}

.order-real-picture-upload__trigger {
  display: block;
  width: 100%;
  aspect-ratio: 1;
}

.order-real-picture-upload__trigger /deep/ .el-upload {
  width: 100%;
  height: 100%;
  border: none;
  background: transparent;
  border-radius: 8px;
}

.order-real-picture-upload__add {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: 1px dashed #c0c4cc;
  border-radius: 8px;
  color: #909399;
  background: #fafafa;
  cursor: pointer;
  box-sizing: border-box;
  font-size: 12px;
}

.order-real-picture-upload__add.is-loading {
  cursor: wait;
}

.order-real-picture-upload__add-icon {
  font-size: 22px;
  line-height: 1;
}

.order-real-picture-upload__add-text {
  max-width: 72px;
  text-align: center;
  line-height: 1.2;
}

.order-real-picture-upload__grid--compact .order-real-picture-upload__thumb,
.order-real-picture-upload__grid--compact .order-real-picture-upload__trigger {
  width: 76px;
  height: 76px;
  aspect-ratio: auto;
}

.order-real-picture-upload__grid--compact .order-real-picture-upload__add-icon {
  font-size: 18px;
}

.order-real-picture-upload__grid--compact .order-real-picture-upload__add-text {
  font-size: 11px;
  max-width: 64px;
}

.order-real-picture-upload__grid--compact .order-real-picture-upload__remove {
  width: 18px;
  height: 18px;
  line-height: 18px;
  font-size: 10px;
}

.order-real-picture-upload__grid--compact + .order-real-picture-upload__hint {
  margin-top: 6px;
}

.order-real-picture-upload__hint {
  margin: 10px 0 0;
  font-size: 12px;
  color: #909399;
  line-height: 1.5;
}
</style>



// WEBPACK FOOTER //
// src/components/orderRealPictureUpload.vue